import { useState } from "react";
import {
  FileSearch, TrendingUp, MessageSquareText, ListOrdered, ShieldCheck,
  Link2, AlertTriangle, MousePointerClick, ExternalLink, Info,
  HelpCircle, RefreshCw, ChevronDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { adminRefreshPaa } from "../lib/blogApi";

const DASH = <span className="text-ink-mute/60">—</span>;

const PAA_WARNING = "Use these questions to understand customer intent and improve the article—not as verified medical evidence.";
const PAA_HELP = [
  "These are related questions people search for on Google. Use them to understand what readers want to know before writing the article.",
  "You do not need to answer every question. Select only the questions that are directly relevant to the article.",
  "Useful questions can help you improve the article outline, write clearer sections or choose FAQs. Verify all medical answers using reliable sources.",
  "Do not copy Google answer text. Do not treat these questions as medical evidence.",
  "The article must still be written manually in your own natural language.",
];
const PAA_REFRESH_CONFIRM = "Refreshing requests new Google People Also Ask data from DataForSEO and may create a small provider charge. The existing questions will remain available if the refresh fails. Continue?";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-surface-line bg-white p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#1f3d2e]" />
        <h3 className="text-[13px] font-bold uppercase tracking-wide text-ink">{title}</h3>
      </div>
      <div className="mt-3 text-[13px] text-ink-soft">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-b border-surface-line/60 py-1.5 last:border-0">
      <span className="text-[12px] font-semibold text-ink-mute">{label}</span>
      <span className="text-[12.5px] text-ink text-right">{value ?? DASH}</span>
    </div>
  );
}

function fmtNum(n) {
  return typeof n === "number" ? n.toLocaleString() : DASH;
}

function PaaSection({ paa, articleId, onPaaUpdated }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshNote, setRefreshNote] = useState(null);
  const status = paa?.status || "not_requested";
  const questions = paa?.questions || [];

  const refresh = async () => {
    if (busy) return;
    if (!window.confirm(PAA_REFRESH_CONFIRM)) return;
    setBusy(true);
    setRefreshNote(null);
    try {
      const r = await adminRefreshPaa(articleId);
      if (r.ok) {
        onPaaUpdated(r.paa);
        toast.success("People Also Ask updated");
      } else {
        onPaaUpdated(r.paa);
        setRefreshNote(r.message || "People Also Ask data could not be refreshed. The previous questions are still available.");
      }
    } catch (e) {
      setRefreshNote(e?.response?.data?.detail || "People Also Ask data could not be refreshed. The previous questions are still available.");
    } finally { setBusy(false); }
  };

  const refreshBtn = (
    <button
      data-testid="paa-refresh"
      onClick={refresh}
      disabled={busy || status === "loading"}
      className="inline-flex items-center gap-1 rounded-lg border border-surface-line px-2 py-1 text-[11px] font-semibold text-ink-mute hover:text-ink disabled:opacity-50"
      title="Request newer Google questions (small provider charge)"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      Refresh PAA
    </button>
  );

  return (
    <section data-testid="brief-paa" className="rounded-xl border border-surface-line bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-[#1f3d2e]" />
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-ink">People Also Ask</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="paa-how-to"
            onClick={() => setHelpOpen((v) => !v)}
            aria-expanded={helpOpen}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1f3d2e] underline underline-offset-2"
          >
            How to use PAA <ChevronDown className={`h-3 w-3 transition-transform ${helpOpen ? "rotate-180" : ""}`} />
          </button>
          {(status === "ready" || status === "failed") && refreshBtn}
        </div>
      </div>

      <p className="mt-1.5 text-[12px] text-ink-mute">Questions people also search for on Google about this topic.</p>
      <p data-testid="paa-warning" className="mt-1 text-[11.5px] font-medium text-amber-800">{PAA_WARNING}</p>

      {helpOpen && (
        <div data-testid="paa-help-box" className="mt-2 rounded-lg border border-[#1f3d2e]/20 bg-cream-50 p-3">
          <ul className="list-disc space-y-1.5 pl-4 text-[12px] text-ink-soft">
            {PAA_HELP.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[13px] text-ink-soft">
        {status === "not_requested" && (
          <p data-testid="paa-not-requested" className="text-ink-mute">
            People Also Ask questions will be collected when research is started.
          </p>
        )}
        {status === "loading" && (
          <p data-testid="paa-loading" className="flex items-center gap-2 text-ink-mute">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Collecting related Google questions for this topic…
          </p>
        )}
        {status === "empty" && (
          <p data-testid="paa-empty" className="text-ink-mute">
            Google did not return People Also Ask questions for this search.
          </p>
        )}
        {status === "failed" && questions.length === 0 && (
          <p data-testid="paa-failed" className="text-red-600">
            People Also Ask data could not be collected. The rest of the Research Brief is still available.
          </p>
        )}
        {refreshNote && (
          <p data-testid="paa-refresh-note" className="mb-2 text-amber-800">{refreshNote}</p>
        )}
        {questions.length > 0 && (
          <>
            <ol className="space-y-1.5" data-testid="paa-question-list">
              {questions.map((q, i) => (
                <li key={`${q.question}-${i}`} className={q.level === 2 ? "ml-4 border-l-2 border-surface-line pl-2.5" : ""}>
                  <span>{q.question}</span>
                  {q.level === 2 && (
                    <span className="ml-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-mute">
                      Related question{q.seed_question ? ` · from “${q.seed_question}”` : ""}
                    </span>
                  )}
                </li>
              ))}
            </ol>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-mute">
              <span>Source: DataForSEO / Google PAA</span>
              {paa.fetched_at && <span>Updated: {new Date(paa.fetched_at).toLocaleString()}</span>}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function ResearchBriefPanel({ brief, loading, articleId, onPaaUpdated }) {
  if (loading) {
    return (
      <div data-testid="research-brief-loading" className="flex items-center gap-2 p-4 text-[13px] text-ink-mute">
        Loading research brief…
      </div>
    );
  }
  if (!brief || brief.linked === false) {
    return (
      <div data-testid="research-brief-not-linked" className="rounded-xl border border-dashed border-surface-line bg-cream-50 p-5 text-center text-[13px] text-ink-mute">
        {brief?.message || "No SEO research brief is linked to this article."}
      </div>
    );
  }

  const t = brief.topic_summary || {};
  const s = brief.main_customer_search || {};
  const structure = brief.suggested_structure || {};

  return (
    <div data-testid="research-brief-panel" className="space-y-4">
      <p className="text-[12px] text-ink-mute">
        Automatically prepared from existing SEO data. The article body stays blank for manual writing.
      </p>

      <Section icon={FileSearch} title="Topic Summary" testid="brief-topic-summary">
        <div className="space-y-0">
          <Row label="Suggested content title" value={t.suggested_title} />
          <Row label="Recommended next step" value={t.recommended_next_step} />
          <Row label="Content type" value={t.content_type} />
          <Row label="Search intent" value={t.search_intent} />
          <Row label="Medication detected" value={t.medication} />
          <Row label="Ingredient detected" value={t.ingredient} />
          <Row label="Skin concern detected" value={t.skin_concern} />
          <Row label="Product / brand detected" value={t.brand_or_product} />
          <Row label="Existing-page status" value={t.existing_page_status} />
        </div>
      </Section>

      <Section icon={TrendingUp} title="Main Customer Search" testid="brief-main-search">
        <div className="space-y-0">
          <Row label="Primary search phrase" value={s.primary_keyword} />
          <Row label="Estimated monthly searches" value={fmtNum(s.monthly_searches)} />
          <Row label="12-month trend" value={s.trend ?? DASH} />
          <Row label="Country" value={s.country} />
          <Row label="Language" value={s.language} />
          <Row label="Priority score" value={s.priority_score} />
          <Row label="Data source" value={s.data_source} />
          <Row label="Data last updated" value={s.data_last_updated ? new Date(s.data_last_updated).toLocaleDateString() : DASH} />
        </div>
        <p className="mt-2 flex items-start gap-1.5 text-[11.5px] text-ink-mute">
          <Info className="mt-0.5 h-3 w-3 shrink-0" /> {s.volume_estimate_note}
        </p>
        <p className="mt-1 text-[11.5px] text-ink-mute">{s.score_explanation}</p>
        {s.gsc_disconnected_message && (
          <p data-testid="brief-gsc-disconnected" className="mt-2 rounded-lg bg-cream-100 px-2.5 py-1.5 text-[11.5px] text-ink-mute">
            {s.gsc_disconnected_message}
          </p>
        )}
      </Section>

      <Section icon={MessageSquareText} title="Related Searches" testid="brief-related-searches">
        {brief.related_searches?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {brief.related_searches.map((k) => (
              <span key={k} className="rounded-full bg-cream-100 px-2.5 py-1 text-[12px] text-ink-soft">{k}</span>
            ))}
          </div>
        ) : DASH}
      </Section>

      <Section icon={MessageSquareText} title="Questions Customers Ask" testid="brief-questions">
        {brief.customer_questions?.length ? (
          <ul className="list-disc space-y-1 pl-5">
            {brief.customer_questions.map((qn) => <li key={qn}>{qn}</li>)}
          </ul>
        ) : (
          <p className="text-ink-mute">{brief.no_questions_message}</p>
        )}
      </Section>

      <PaaSection paa={brief.paa} articleId={articleId} onPaaUpdated={onPaaUpdated} />

      <Section icon={ListOrdered} title="Suggested Article Structure" testid="brief-structure">
        <ol className="list-decimal space-y-1 pl-5">
          {(structure.headings || []).map((h) => <li key={h}>{h}</li>)}
        </ol>
        <p className="mt-2 text-[11.5px] italic text-ink-mute">{structure.note}</p>
      </Section>

      <Section icon={ShieldCheck} title="Evidence Checklist" testid="brief-evidence">
        <ul className="space-y-1">
          {(brief.evidence_checklist || []).map((c) => (
            <li key={c} className="flex items-start gap-2">
              <span className="mt-0.5 inline-block h-3.5 w-3.5 shrink-0 rounded border border-ink-mute" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3">
          {brief.stored_source_urls?.length ? (
            <ul className="list-disc space-y-1 pl-5">
              {brief.stored_source_urls.map((u) => (
                <li key={u}><a href={u} target="_blank" rel="noreferrer" className="text-[#1f3d2e] underline">{u}</a></li>
              ))}
            </ul>
          ) : (
            <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-800">{brief.no_sources_message}</p>
          )}
        </div>
      </Section>

      <Section icon={Link2} title="Internal-Link Suggestions" testid="brief-internal-links">
        {brief.internal_link_suggestions?.length ? (
          <ul className="space-y-1.5">
            {brief.internal_link_suggestions.map((l) => (
              <li key={l.url + l.kind} className="flex items-center gap-2">
                <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1f3d2e] underline">
                  {l.label} <ExternalLink className="h-3 w-3" />
                </a>
                <span className="text-[11px] text-ink-mute">({l.url})</span>
              </li>
            ))}
          </ul>
        ) : DASH}
      </Section>

      {brief.duplicate_content_warning && (
        <div data-testid="brief-duplicate-warning" className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-amber-800">Duplicate-Content Warning</h3>
          </div>
          <p className="mt-2 text-[12.5px] text-amber-900">{brief.duplicate_content_warning.message}</p>
          {brief.duplicate_content_warning.existing_page_url && (
            <a href={brief.duplicate_content_warning.existing_page_url} target="_blank" rel="noreferrer"
               className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-amber-800 underline">
              {brief.duplicate_content_warning.existing_page_url} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      <Section icon={MousePointerClick} title="Recommended Reader Action" testid="brief-cta">
        <p>{brief.recommended_reader_action}</p>
        <p className="mt-1.5 text-[11.5px] italic text-ink-mute">
          Guidance for Chutki — this is not inserted into the article automatically.
        </p>
      </Section>
    </div>
  );
}
